import React, { useState } from 'react';
import { X, Video, Upload, Sparkles, Building2, Film } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card } from './ui/Card';
import { Badge } from './ui/Badge';
import { cn } from '../lib/utils';
interface AddInspirationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (inspiration: {
    title: string;
    videoFile: File | null;
    topic: string;
    feel: string[];
    aiContext: string;
    brandProfile: string;
    formatProfile: string;
  }) => void;
}
const TOPIC_OPTIONS = ['Financial Education', 'Gen Z Banking', 'Credit & Loans', 'Savings Tips', 'Banking Secrets', 'Mortgages', 'Investing', 'Banking Tips'];
const FEEL_OPTIONS = ['Inspiring', 'Educational', 'Authentic', 'Professional', 'Casual', 'Energetic', 'Calm', 'Bold', 'Creative', 'Trendy'];
const BRAND_PROFILES = ['einfachbank Professional', 'einfachbank Gen Z'];
const FORMAT_PROFILES: {
  [key: string]: string[];
} = {
  'einfachbank Professional': ['Talking Head', 'Sketch Explainer'],
  'einfachbank Gen Z': ['Whiteboard Session', 'Casual Talking Head']
};
export function AddInspirationModal({
  isOpen,
  onClose,
  onSubmit
}: AddInspirationModalProps) {
  const [title, setTitle] = useState('');
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [topic, setTopic] = useState('');
  const [selectedFeel, setSelectedFeel] = useState<string[]>([]);
  const [aiContext, setAiContext] = useState('');
  const [brandProfile, setBrandProfile] = useState('');
  const [formatProfile, setFormatProfile] = useState('');
  if (!isOpen) return null;
  const availableFormats = brandProfile ? FORMAT_PROFILES[brandProfile] : [];
  const handleBrandChange = (brand: string) => {
    setBrandProfile(brand);
    setFormatProfile(''); // Reset format when brand changes
  };
  const handleVideoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
    }
  };
  const toggleFeel = (feel: string) => {
    if (selectedFeel.includes(feel)) {
      setSelectedFeel(selectedFeel.filter(f => f !== feel));
    } else {
      setSelectedFeel([...selectedFeel, feel]);
    }
  };
  const handleSubmit = () => {
    if (!title.trim() || !videoFile || !topic || selectedFeel.length === 0 || !aiContext.trim() || !brandProfile || !formatProfile) return;
    onSubmit({
      title,
      videoFile,
      topic,
      feel: selectedFeel,
      aiContext,
      brandProfile,
      formatProfile
    });
    // Reset form
    setTitle('');
    setVideoFile(null);
    setVideoPreview(null);
    setTopic('');
    setSelectedFeel([]);
    setAiContext('');
    setBrandProfile('');
    setFormatProfile('');
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-id="element-97">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col bg-white" data-id="element-98">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" data-id="element-99">
          <div className="flex items-center gap-3" data-id="element-100">
            <div className="h-10 w-10 rounded-lg bg-[#139191]/10 flex items-center justify-center" data-id="element-101">
              <Video className="h-5 w-5 text-[#139191]" data-id="element-102" />
            </div>
            <div data-id="element-103">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-104">
                Add Inspiration
              </h2>
              <p className="text-sm text-gray-500" data-id="element-105">
                Save a video to your swipefile with AI context
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-id="element-106">
            <X className="h-5 w-5" data-id="element-107" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-108">
          {/* Video Upload */}
          <div data-id="element-109">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-110">
              Upload Video *
            </label>
            {!videoPreview ? <label className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#139191] hover:bg-gray-50 transition-colors" data-id="element-111">
                <div className="flex flex-col items-center justify-center pt-5 pb-6" data-id="element-112">
                  <Upload className="h-12 w-12 text-gray-400 mb-3" data-id="element-113" />
                  <p className="mb-2 text-sm text-gray-600" data-id="element-114">
                    <span className="font-semibold" data-id="element-115">Click to upload</span> or
                    drag and drop
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-116">
                    MP4, MOV, or WebM (MAX. 100MB)
                  </p>
                </div>
                <input type="file" className="hidden" accept="video/*" onChange={handleVideoUpload} data-id="element-117" />
              </label> : <div className="relative" data-id="element-118">
                <video src={videoPreview} controls className="w-full rounded-lg" style={{
              maxHeight: '300px'
            }} data-id="element-119" />
                <Button variant="outline" size="sm" className="absolute top-2 right-2 bg-white/90 backdrop-blur" onClick={() => {
              setVideoFile(null);
              setVideoPreview(null);
            }} data-id="element-120">
                  Change Video
                </Button>
              </div>}
          </div>

          {/* Brand Profile */}
          <div data-id="element-121">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-122">
              <Building2 className="h-4 w-4 text-[#139191]" data-id="element-123" />
              Brand Profile *
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-124">
              {BRAND_PROFILES.map(brand => <button key={brand} onClick={() => handleBrandChange(brand)} className={cn('px-4 py-2 rounded-lg text-sm font-medium transition-all border', brandProfile === brand ? 'bg-[#139191] text-white border-[#139191]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#139191] hover:text-[#139191]')} data-id="element-125">
                  {brand}
                </button>)}
            </div>
          </div>

          {/* Format Profile */}
          {brandProfile && <div data-id="element-126">
              <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-127">
                <Film className="h-4 w-4 text-purple-600" data-id="element-128" />
                Format Profile *
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-129">
                {availableFormats.map(format => <button key={format} onClick={() => setFormatProfile(format)} className={cn('px-4 py-2 rounded-lg text-sm font-medium transition-all border', formatProfile === format ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-700 border-gray-200 hover:border-purple-600 hover:text-purple-600')} data-id="element-130">
                    {format}
                  </button>)}
              </div>
            </div>}

          {/* Title */}
          <div data-id="element-131">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-132">
              Video Title *
            </label>
            <Input placeholder="e.g., How Compound Interest Actually Works" value={title} onChange={e => setTitle(e.target.value)} className="text-base" data-id="element-133" />
          </div>

          {/* Topic */}
          <div data-id="element-134">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-135">
              Topic *
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-136">
              {TOPIC_OPTIONS.map(topicOption => <button key={topicOption} onClick={() => setTopic(topicOption)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${topic === topicOption ? 'bg-[#139191] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-137">
                  {topicOption}
                </button>)}
            </div>
          </div>

          {/* Feel */}
          <div data-id="element-138">
            <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-139">
              Feel (Select multiple) *
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-140">
              {FEEL_OPTIONS.map(feel => <button key={feel} onClick={() => toggleFeel(feel)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedFeel.includes(feel) ? 'bg-[#139191] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-141">
                  {feel}
                </button>)}
            </div>
          </div>

          {/* AI Context */}
          <div data-id="element-142">
            <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" data-id="element-143">
              <Sparkles className="h-4 w-4 text-[#139191]" data-id="element-144" />
              What makes this inspiring? *
            </label>
            <p className="text-xs text-gray-500 mb-3" data-id="element-145">
              Help the AI understand why you're saving this. What elements,
              techniques, or ideas should it learn from this video?
            </p>
            <textarea placeholder="e.g., The hook in the first 3 seconds is incredibly effective - it uses a question that creates curiosity. The pacing is perfect for short-form content, and the visual transitions keep viewers engaged. The storytelling structure follows a clear problem-solution format that I want to replicate." value={aiContext} onChange={e => setAiContext(e.target.value)} rows={6} className="w-full px-3 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#139191] focus:border-transparent resize-none text-sm" data-id="element-146" />
            <p className="text-xs text-gray-500 mt-2" data-id="element-147">
              💡 Tip: Be specific about hooks, pacing, visual style,
              storytelling techniques, or any elements you want the AI to
              reference.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50" data-id="element-148">
          <p className="text-sm text-gray-500" data-id="element-149">
            {selectedFeel.length > 0 && `${selectedFeel.length} feel${selectedFeel.length > 1 ? 's' : ''} selected`}
          </p>
          <div className="flex gap-3" data-id="element-150">
            <Button variant="outline" onClick={onClose} data-id="element-151">
              Cancel
            </Button>
            <Button className="bg-[#139191] hover:bg-[#139191]/90 text-white" onClick={handleSubmit} disabled={!title.trim() || !videoFile || !topic || selectedFeel.length === 0 || !aiContext.trim() || !brandProfile || !formatProfile} data-id="element-152">
              Add to Swipefile
            </Button>
          </div>
        </div>
      </Card>
    </div>;
}